import { cn, NoddiIcon, NoddiIconName } from 'noddi-ui-common';
import { commonTexts, type Currency } from 'noddi-util';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { noddiToast } from '../Feedback/NoddiToaster/toast';
import { NoddiButton } from '../Inputs/NoddiButton';
import { formatInfoValue, getInfoTextToCopy, InfoValueType } from '../Other/infoFieldValue';
import { NoddiTooltip } from '../Overlay/NoddiTooltip';
import { NoddiCard } from './NoddiCard';

type Translations = typeof commonTexts.infoCard;

export type NavioInfoCardItem = {
  label: string;
  /** Empty (`null`, `undefined`, `''`) shows "Add <label>" when the card has `onEdit`, else a dash. */
  value?: ReactNode;
  type?: InfoValueType;
  currency?: Currency;
  /**
   * Makes the value a link: an app path (`/…`) stays in the tab; `mailto:` / `tel:` hand off to the
   * device; anything else is a website, opened in a new tab with an external icon.
   */
  href?: string;
  /**
   * What the copy action puts on the clipboard. Defaults to the shown text when the value is text,
   * a number or a formatted `type`; `false` removes the action, e.g. for a status or a yes/no.
   */
  copyText?: string | false;
  tooltip?: string;
  /**
   * The card's edit cannot set this row (computed or system-set, e.g. "Approved at"), so an empty
   * value shows a dash instead of "Add <label>".
   */
  readOnly?: boolean;
  /** Spans every column of a `columns` grid, for a value too wide for one, e.g. a priced list under short facts. */
  fullWidth?: boolean;
};

type Props = {
  /** Omitted when something joined onto the card already names it, e.g. a hero band above it. */
  title?: string;
  /** What the facts are about, under the title, e.g. which rules a set of parameters feeds. */
  description?: string;
  icon?: NoddiIconName;
  /** Rows in order. Falsy entries are skipped, so `condition && { … }` hides a row. */
  items: (NavioInfoCardItem | false | null | undefined)[];
  /** Two or three columns on wider screens, for a card of many short facts. One column on phones, always. */
  columns?: 1 | 2 | 3;
  /**
   * `compact` puts the label and value on one line and tightens the rows, for a dense fact card.
   * Below `md` the rows stack like `default`: a phone has no width to spare for a label column.
   */
  density?: 'default' | 'compact';
  /** The card's one edit entry, shown as Edit in the header. Rows never carry their own edit. */
  onEdit?: () => void;
  /** Header actions beside Edit, e.g. a `NoddiLink` to the parent resource. */
  headerNode?: ReactNode;
  /** Content under the rows that is not a label/value fact, e.g. a note or an image. */
  children?: ReactNode;
  /** Shown beside the rows from `md`, above them on phones, e.g. the photo of the car the facts describe. */
  media?: ReactNode;
  /** Edge-to-edge muted strip closing the card, for reference facts under the main ones, e.g. a car's specifications. */
  footer?: ReactNode;
  translations?: Partial<Translations>;
  className?: string;
};

/**
 * A card of label/value facts. The card owns everything: the title, the single Edit action, the row
 * layout, value formatting, links and copy. Desktop reveals copy on row hover; touch shows it always.
 */
export const NavioInfoCard = ({
  title,
  description,
  icon,
  items,
  columns = 1,
  density = 'default',
  onEdit,
  headerNode,
  children,
  media,
  footer,
  translations,
  className
}: Props) => {
  const texts = { ...commonTexts.infoCard, ...translations };
  const visibleItems = items.filter((item): item is NavioInfoCardItem => !!item);
  const hasHeaderActions = !!onEdit || !!headerNode;
  const gapClassName = density === 'compact' ? 'gap-2' : 'gap-3';
  const facts = (
    <>
      {visibleItems.length > 0 && (
        <dl
          className={cn(
            // One divider per row only while the facts are one column; a grid rakes them across columns.
            columns === 1
              ? 'max-md:divide-y max-md:divide-outlineStroke'
              : 'max-sm:divide-y max-sm:divide-outlineStroke',
            density === 'compact' ? 'md:gap-0' : 'md:gap-1',
            columns === 1 && 'flex flex-col',
            columns === 2 && 'grid grid-cols-1 gap-x-6 sm:grid-cols-2',
            columns === 3 && 'grid grid-cols-1 gap-x-6 sm:grid-cols-2 lg:grid-cols-3'
          )}
        >
          {visibleItems.map((item, index) => (
            // Labels can repeat (a payer's and a contact's phone), so the position keeps keys unique.
            <InfoRow key={`${index}-${item.label}`} item={item} density={density} onEdit={onEdit} texts={texts} />
          ))}
        </dl>
      )}
      {children}
    </>
  );

  return (
    <NoddiCard
      size='small'
      title={title}
      description={description}
      icon={icon}
      iconTile={!!icon}
      className={className}
      footer={footer}
      contentClassName={cn(
        media ? 'grid items-center gap-5 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]' : ['flex flex-col', gapClassName]
      )}
      headerNode={
        hasHeaderActions && (
          <div className='flex items-center gap-2'>
            {headerNode}
            {onEdit && (
              <NoddiButton
                variant='outline'
                size='small'
                startIcon='Edit'
                aria-label={title ? texts.editCard(title) : texts.edit}
                onClick={onEdit}
              >
                {texts.edit}
              </NoddiButton>
            )}
          </div>
        )
      }
    >
      {media ? (
        <>
          {media}
          <div className={cn('flex min-w-0 flex-col', gapClassName)}>{facts}</div>
        </>
      ) : (
        facts
      )}
    </NoddiCard>
  );
};

const COPIED_FEEDBACK_MS = 1600;

const InfoRow = ({
  item,
  density,
  onEdit,
  texts
}: {
  item: NavioInfoCardItem;
  density: 'default' | 'compact';
  onEdit?: () => void;
  texts: Translations;
}) => {
  const isCompact = density === 'compact';
  const { label, value, type, currency, href, copyText, tooltip, readOnly } = item;
  const [isCopied, setIsCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const isEmpty = value === null || value === undefined || value === '';
  const formattedValue = formatInfoValue(value, type, currency);
  const textToCopy =
    isEmpty || copyText === false || type === 'boolean'
      ? ''
      : (copyText ?? getInfoTextToCopy(value, formattedValue, type));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
    } catch {
      noddiToast.error(texts.copyFailed);
    }
  };

  return (
    <div
      className={cn(
        'group/row flex min-w-0 items-start gap-3 md:-mx-2.5 md:rounded-lg md:px-2.5 md:transition-colors md:hover:bg-darkPurpleGrey',
        isCompact ? 'py-2 md:py-1' : 'py-2',
        item.fullWidth && 'col-span-full'
      )}
    >
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-0.5',
          isCompact && 'md:flex-row md:items-baseline md:justify-between md:gap-3'
        )}
      >
        <dt className={cn('flex items-center gap-1 text-xs text-greyMuted', isCompact && 'md:flex-none')}>
          {label}
          {tooltip && <NoddiTooltip content={tooltip} />}
        </dt>
        <dd
          className={cn(
            'text-sm text-pretty whitespace-pre-line text-foreground',
            // A value sharing its line with the label must not use `overflow-wrap: anywhere`: that
            // drops its min-content width to one character, and flexbox takes it, leaving a column
            // one letter wide. `break-words` still breaks an id too long for the line.
            isCompact ? 'break-words md:text-right' : '[overflow-wrap:anywhere]'
          )}
        >
          {isEmpty ? (
            onEdit && !readOnly ? (
              <button
                type='button'
                onClick={onEdit}
                className='flex items-center gap-1 font-medium text-primary hover:underline'
              >
                <NoddiIcon name='Plus' size='small' />
                {texts.add(label)}
              </button>
            ) : (
              <span className='text-muted-foreground'>-</span>
            )
          ) : href ? (
            <InfoLink href={href} texts={texts}>
              {formattedValue}
            </InfoLink>
          ) : (
            formattedValue
          )}
        </dd>
      </div>
      {textToCopy && (
        // Desktop with a mouse reveals the action on hover or keyboard focus; phone widths and touch keep it visible.
        <div
          data-copied={isCopied}
          className='flex flex-none items-center gap-1 transition-opacity data-[copied=true]:opacity-100 md:pointer-fine:opacity-0 md:pointer-fine:group-focus-within/row:opacity-100 md:pointer-fine:group-hover/row:opacity-100'
        >
          <span aria-live='polite' className='text-xs font-medium text-primary'>
            {isCopied && texts.copied}
          </span>
          <button
            type='button'
            aria-label={texts.copy(label)}
            title={texts.copy(label)}
            // oxlint-disable-next-line typescript/no-misused-promises -- copy() catches its own failure, so the discarded promise never rejects
            onClick={copy}
            className={cn(
              'flex size-7 items-center justify-center rounded-md text-darkPurple70 transition-colors hover:bg-darkPurple30 hover:text-darkPurple',
              // 44px tap target on phones and touch, pulled into the row padding so it does not grow the row.
              'max-md:-my-2 max-md:-mr-2 max-md:size-11 pointer-coarse:-my-2 pointer-coarse:-mr-2 pointer-coarse:size-11',
              isCopied && 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary'
            )}
          >
            <NoddiIcon name={isCopied ? 'Check' : 'Copy'} size='small' />
          </button>
        </div>
      )}
    </div>
  );
};

const DEVICE_HREF = /^(mailto|tel):/;
const HAS_SCHEME = /^[a-z][a-z\d+.-]*:/i;

const InfoLink = ({ href, texts, children }: { href: string; texts: Translations; children: ReactNode }) => {
  const className =
    'font-medium text-primary underline decoration-primary/40 underline-offset-3 transition-colors hover:decoration-primary';

  if (href.startsWith('/')) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  if (DEVICE_HREF.test(href)) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }
  // Websites are often saved without a scheme ("navio.no"), which the browser would resolve as an app path.
  return (
    <a href={HAS_SCHEME.test(href) ? href : `https://${href}`} target='_blank' rel='noreferrer' className={className}>
      {children}
      <NoddiIcon name='SquareArrowOutUpRight' size='xSmall' className='ml-1 inline align-[-1px]' />
      <span className='sr-only'>{texts.opensInNewTab}</span>
    </a>
  );
};
